import { Column, DataTable } from '@/shared/components/tables/DataTable';
import { useDataTableState } from '@/shared/components/tables/useDataTableState';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useVoltarAuditorias } from '@/modules/system/auditorias/use-voltar-auditorias';
import { GetUsuarios } from '@/modules/system/usuario/usuario.service';
import type { ReadUsuarioDto } from '@/api.types';
import { GetAuditoriaEmails } from './auditoria-emails.service';
import type { AuditoriaEmailDto, FindAuditoriaEmailsDto } from './auditoria-emails.types';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale/pt-BR';
import { useSearchParams } from 'react-router-dom';

function periodoPadrao() {
  const hoje = new Date();
  return {
    inicio: new Date(hoje.getFullYear(), hoje.getMonth(), 1),
    fim: new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0),
  };
}

function dataDaUrl(valor: string | null, fallback: Date) {
  if (!valor || !/^\d{4}-\d{2}-\d{2}$/.test(valor)) return fallback;
  const [ano, mes, dia] = valor.split('-').map(Number);
  return new Date(ano, mes - 1, dia);
}

function dataParaUrl(data: Date) {
  if (!(data instanceof Date) || Number.isNaN(data.getTime())) return '';
  const mes = String(data.getMonth() + 1).padStart(2, '0');
  const dia = String(data.getDate()).padStart(2, '0');
  return `${data.getFullYear()}-${mes}-${dia}`;
}

export default function LogsEmailsPage() {
  useVoltarAuditorias();
  const [searchParams, setSearchParams] = useSearchParams();
  const padrao = periodoPadrao();

  const [loaded, setLoaded] = useState(false);
  const [buscando, setBuscando] = useState(false);

  const [dataInicial, setDataInicial] = useState(() => dataDaUrl(searchParams.get('inicio'), padrao.inicio));
  const [dataFinal, setDataFinal] = useState(() => dataDaUrl(searchParams.get('fim'), padrao.fim));
  const [destinatario, setDestinatario] = useState(() => searchParams.get('destinatario') ?? '');
  const [acao, setAcao] = useState(() => searchParams.get('acao') ?? '');
  const [codigo, setCodigo] = useState(() => searchParams.get('codigo') ?? '');
  const [idUsuario, setIdUsuario] = useState(() => Number(searchParams.get('usuario')) || 0);
  const [listaUsuarios, setListaUsuarios] = useState<ReadUsuarioDto[]>([]);

  const [registros, setRegistros] = useState<[AuditoriaEmailDto[], number]>();

  const tableState = useDataTableState('enviadoEm');

  useEffect(() => {
    GetUsuarios({ skip: 0, take: 1000 })
      .then((result) => {
        const ordenados = result[0].sort((a, b) => a.username!.localeCompare(b.username!));
        setListaUsuarios(ordenados);
      })
      .catch((e) => toast.error(String(e)))
      .finally(() => setLoaded(true));
  }, []);

  useEffect(() => {
    if (!loaded) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('page', String(tableState.currentPage));
      if (idUsuario > 0) next.set('usuario', String(idUsuario));
      else next.delete('usuario');
      if (destinatario.trim()) next.set('destinatario', destinatario.trim());
      else next.delete('destinatario');
      if (acao.trim()) next.set('acao', acao.trim());
      else next.delete('acao');
      if (codigo.trim()) next.set('codigo', codigo.trim());
      else next.delete('codigo');
      const inicio = dataParaUrl(dataInicial);
      const fim = dataParaUrl(dataFinal);
      if (inicio) next.set('inicio', inicio);
      else next.delete('inicio');
      if (fim) next.set('fim', fim);
      else next.delete('fim');
      return next;
    }, { replace: true });
  }, [
    loaded,
    idUsuario,
    destinatario,
    acao,
    codigo,
    dataInicial,
    dataFinal,
    tableState.currentPage,
    setSearchParams,
  ]);

  const columns: Column<AuditoriaEmailDto>[] = useMemo(
    () => [
      {
        key: 'enviadoEm',
        header: 'Data',
        render: (row) => format(new Date(row.enviadoEm), 'dd/MM/yyyy HH:mm', { locale: ptBR }),
      },
      {
        key: 'destinatarioEmail',
        header: 'Destinatário',
        render: (row) => <span className="font-monospace small">{row.destinatarioEmail}</span>,
      },
      { key: 'acao', header: 'Ação' },
      {
        key: 'codigo',
        header: 'Código',
        render: (row) => {
          const codigoNorm = (row.codigo || '').toUpperCase();
          const classe =
            codigoNorm === 'SUCESSO'
              ? 'bg-success'
              : codigoNorm === 'PENDENTE'
                ? 'bg-warning text-dark'
                : 'bg-danger';
          return <span className={`badge ${classe}`}>{row.codigo}</span>;
        },
      },
      {
        key: 'usuario.username',
        header: 'Remetente (usuário)',
        render: (row) => row.usuario?.username || 'Sistema',
      },
    ],
    [],
  );

  async function handleBuscar() {
    if (!loaded) return;

    setBuscando(true);
    try {
      const filters: FindAuditoriaEmailsDto = {
        skip: (tableState.currentPage - 1) * tableState.pageSize,
        take: tableState.pageSize,
        data_inicial: dataInicial.toISOString(),
        data_final: dataFinal.toISOString(),
        destinatario_email: destinatario.trim() || undefined,
        acao: acao.trim() || undefined,
        codigo: codigo.trim() || undefined,
        usuario_id: idUsuario || undefined,
      };

      const result = await GetAuditoriaEmails(filters);
      setRegistros(result);
    } catch (e) {
      toast.error(String(e));
    } finally {
      setBuscando(false);
    }
  }

  useEffect(() => {
    void handleBuscar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, tableState.currentPage, tableState.pageSize, tableState.sortConfig]);

  return (
    <div className="row">
      <div className="col-12">
        <h3>Logs de E-mails</h3>
        <span className="blockquote-footer pb-4 text-end">
          Auditoria de envios de e-mail (tabela <code>auditoria_emails</code>).
        </span>
      </div>
      <hr />

      {registros && (
        <div className="col-12">
          <DataTable
            columns={columns}
            data={registros[0]}
            onChange={handleBuscar}
            serverSide={true}
            showFilter={false}
            states={tableState}
            totalRecords={registros[1]}
            renderFilters={() => (
              <div className="row">
                <div className="col-md-3">
                  <label htmlFor="usuario-email" className="col-form-label">
                    Usuário remetente:
                  </label>
                  <select
                    id="usuario-email"
                    className="form-select"
                    value={idUsuario}
                    onChange={(e) => setIdUsuario(Number(e.target.value))}
                  >
                    <option value={0}>Sistema</option>
                    {listaUsuarios.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.username}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-3">
                  <label htmlFor="destinatario-email" className="col-form-label">
                    Destinatário contém:
                  </label>
                  <input
                    id="destinatario-email"
                    className="form-control font-monospace"
                    value={destinatario}
                    onChange={(e) => setDestinatario(e.target.value)}
                    placeholder="ex.: @defensoria.se.def.br"
                  />
                </div>

                <div className="col-md-3">
                  <label htmlFor="acao-email" className="col-form-label">
                    Ação contém:
                  </label>
                  <input
                    id="acao-email"
                    className="form-control"
                    value={acao}
                    onChange={(e) => setAcao(e.target.value)}
                    placeholder="ex.: Recuperar senha"
                  />
                </div>

                <div className="col-md-2">
                  <label htmlFor="codigo-email" className="col-form-label">
                    Código:
                  </label>
                  <input
                    id="codigo-email"
                    className="form-control font-monospace"
                    value={codigo}
                    onChange={(e) => setCodigo(e.target.value)}
                    placeholder="SUCESSO / PENDENTE / FALHA"
                  />
                </div>

                <div className="col-md-2">
                  <label htmlFor="data-inicial-email" className="col-form-label">
                    Data inicial:
                  </label>
                  <input
                    id="data-inicial-email"
                    type="date"
                    className="form-control"
                    value={dataParaUrl(dataInicial)}
                    onChange={(e) => setDataInicial(dataDaUrl(e.target.value, dataInicial))}
                  />
                </div>

                <div className="col-md-2">
                  <label htmlFor="data-final-email" className="col-form-label">
                    Data final:
                  </label>
                  <input
                    id="data-final-email"
                    type="date"
                    className="form-control"
                    value={dataParaUrl(dataFinal)}
                    onChange={(e) => setDataFinal(dataDaUrl(e.target.value, dataFinal))}
                  />
                </div>

                <div className="col-12 mt-3">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-success"
                    onClick={handleBuscar}
                    disabled={buscando}
                  >
                    {buscando ? 'Buscando...' : 'Pesquisar'}
                  </button>
                </div>
              </div>
            )}
          />
        </div>
      )}
    </div>
  );
}
